import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

const PoliticaPrivacidade = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="rounded-xl p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Política de Privacidade</h1>
                <p className="text-sm text-gray-500">Última atualização: Abril de 2026</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-10">
          <div className="prose prose-gray max-w-none">
            
            {/* LGPD Badge */}
            <div className="not-prose mb-8 p-4 bg-green-50 rounded-xl border border-green-200 flex items-center gap-3">
              <Shield className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-800 mb-0">Em conformidade com a LGPD</p>
                <p className="text-sm text-green-700 mb-0">
                  Lei Geral de Proteção de Dados (Lei nº 13.709/2018)
                </p>
              </div>
            </div>

            <h2>1. Introdução</h2>
            <p>
              O <strong>Lembrou?</strong> ("nós", "nosso" ou "Plataforma") está comprometido em proteger 
              a privacidade e os dados pessoais de nossos usuários. Esta Política de Privacidade 
              descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais, 
              em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>

            <h2>2. Dados que Coletamos</h2>
            <p>
              Coletamos os seguintes tipos de dados pessoais:
            </p>
            
            <h3>2.1. Dados fornecidos por você:</h3>
            <ul>
              <li><strong>Dados de cadastro:</strong> Nome, email, telefone, CPF/CNPJ, tipo de profissão</li>
              <li><strong>Dados de clientes:</strong> Nome, email, telefone dos clientes que você cadastra</li>
              <li><strong>Dados de lembretes:</strong> Tipo de serviço, datas de vencimento e renovação</li>
              <li><strong>Dados de pagamento:</strong> Processados pelo Stripe (não armazenamos dados de cartão)</li>
            </ul>

            <h3>2.2. Dados coletados automaticamente:</h3>
            <ul>
              <li><strong>Dados de uso:</strong> Páginas visitadas, funcionalidades utilizadas, horários de acesso</li>
              <li><strong>Dados técnicos:</strong> Endereço IP, tipo de navegador, sistema operacional</li>
              <li><strong>Cookies:</strong> Utilizamos cookies essenciais para funcionamento da plataforma</li>
            </ul>

            <h2>3. Como Usamos seus Dados</h2>
            <p>
              Utilizamos seus dados pessoais para as seguintes finalidades:
            </p>
            <ul>
              <li><strong>Prestação do serviço:</strong> Enviar lembretes por email e WhatsApp</li>
              <li><strong>Comunicação:</strong> Enviar notificações sobre sua conta e atualizações</li>
              <li><strong>Pagamentos:</strong> Processar assinaturas e cobranças</li>
              <li><strong>Melhorias:</strong> Analisar uso para aprimorar a Plataforma</li>
              <li><strong>Segurança:</strong> Prevenir fraudes e atividades suspeitas</li>
              <li><strong>Obrigações legais:</strong> Cumprir exigências legais e regulatórias</li>
            </ul>

            <h2>4. Base Legal para Tratamento (LGPD)</h2>
            <p>
              O tratamento de seus dados pessoais é fundamentado nas seguintes bases legais da LGPD:
            </p>
            <ul>
              <li><strong>Execução de contrato (Art. 7º, V):</strong> Para prestação dos serviços contratados</li>
              <li><strong>Consentimento (Art. 7º, I):</strong> Quando você aceita esta política</li>
              <li><strong>Legítimo interesse (Art. 7º, IX):</strong> Para melhorias e segurança da Plataforma</li>
              <li><strong>Cumprimento de obrigação legal (Art. 7º, II):</strong> Para atender exigências legais</li>
            </ul>

            <h2>5. Compartilhamento de Dados</h2>
            <p>
              Seus dados podem ser compartilhados com:
            </p>
            <ul>
              <li><strong>Stripe:</strong> Processamento de pagamentos (PCI-DSS compliant)</li>
              <li><strong>Resend:</strong> Envio de emails transacionais</li>
              <li><strong>Z-API:</strong> Envio de mensagens WhatsApp</li>
              <li><strong>Cloudflare:</strong> Hospedagem e CDN</li>
              <li><strong>Google Analytics:</strong> Análise de uso (dados anonimizados)</li>
            </ul>
            <p>
              <strong>Não vendemos, alugamos ou comercializamos seus dados pessoais.</strong>
            </p>

            <h2>6. Armazenamento e Segurança</h2>
            <p>
              Implementamos medidas técnicas e organizacionais para proteger seus dados:
            </p>
            <ul>
              <li>Criptografia em trânsito (HTTPS/TLS)</li>
              <li>Criptografia em repouso para dados sensíveis</li>
              <li>Controle de acesso baseado em funções</li>
              <li>Monitoramento e logs de segurança</li>
              <li>Backups regulares</li>
            </ul>
            <p>
              Seus dados são armazenados em servidores seguros com infraestrutura de nuvem certificada.
            </p>

            <h2>7. Retenção de Dados</h2>
            <p>
              Mantemos seus dados pelo tempo necessário para:
            </p>
            <ul>
              <li><strong>Dados da conta:</strong> Enquanto a conta estiver ativa</li>
              <li><strong>Dados de lembretes:</strong> Enquanto a conta estiver ativa ou conforme solicitado</li>
              <li><strong>Dados de pagamento:</strong> Conforme exigências fiscais (5 anos)</li>
              <li><strong>Logs de acesso:</strong> 6 meses (Marco Civil da Internet)</li>
            </ul>
            <p>
              Após o encerramento da conta, seus dados serão excluídos ou anonimizados em até 30 dias, 
              exceto quando houver obrigação legal de retenção.
            </p>

            <h2>8. Seus Direitos (LGPD)</h2>
            <p>
              Você tem os seguintes direitos garantidos pela LGPD:
            </p>
            <ul>
              <li><strong>Confirmação e acesso:</strong> Saber se tratamos seus dados e acessá-los</li>
              <li><strong>Correção:</strong> Corrigir dados incompletos, inexatos ou desatualizados</li>
              <li><strong>Anonimização ou exclusão:</strong> Solicitar anonimização ou exclusão de dados desnecessários</li>
              <li><strong>Portabilidade:</strong> Receber seus dados em formato estruturado</li>
              <li><strong>Eliminação:</strong> Solicitar exclusão de dados tratados com consentimento</li>
              <li><strong>Informação:</strong> Saber com quem compartilhamos seus dados</li>
              <li><strong>Revogação:</strong> Revogar consentimento a qualquer momento</li>
              <li><strong>Oposição:</strong> Opor-se ao tratamento em determinadas situações</li>
            </ul>
            <p>
              Para exercer seus direitos, entre em contato pelo email: <strong>privacidade@lembrou.com.br</strong>
            </p>

            <h2>9. Cookies</h2>
            <p>
              Utilizamos cookies essenciais para:
            </p>
            <ul>
              <li>Manter você logado na Plataforma</li>
              <li>Lembrar suas preferências</li>
              <li>Garantir segurança da sessão</li>
            </ul>
            <p>
              Cookies de análise (Google Analytics) coletam dados anonimizados sobre uso da Plataforma. 
              Você pode desabilitá-los nas configurações do seu navegador.
            </p>

            <h2>10. Transferência Internacional</h2>
            <p>
              Alguns de nossos prestadores de serviço podem estar localizados fora do Brasil. 
              Nesses casos, garantimos que a transferência ocorre de acordo com a LGPD, 
              utilizando cláusulas contratuais padrão ou outros mecanismos legais adequados.
            </p>

            <h2>11. Menores de Idade</h2>
            <p>
              O <strong>Lembrou?</strong> não é destinado a menores de 18 anos. 
              Não coletamos intencionalmente dados de menores. Se você acredita que coletamos 
              dados de um menor, entre em contato imediatamente.
            </p>

            <h2>12. Alterações nesta Política</h2>
            <p>
              Podemos atualizar esta Política periodicamente. Alterações significativas serão 
              comunicadas por email ou através da Plataforma. Recomendamos revisar esta página regularmente.
            </p>

            <h2>13. Encarregado de Dados (DPO)</h2>
            <p>
              Para questões relacionadas à proteção de dados, entre em contato com nosso 
              Encarregado de Dados:
            </p>
            <ul>
              <li><strong>Email:</strong> privacidade@lembrou.com.br</li>
              <li><strong>Assunto:</strong> [LGPD] Sua solicitação</li>
            </ul>

            <h2>14. Contato</h2>
            <p>
              Em caso de dúvidas sobre esta Política de Privacidade:
            </p>
            <ul>
              <li><strong>Email geral:</strong> contato@lembrou.com.br</li>
              <li><strong>Email privacidade:</strong> privacidade@lembrou.com.br</li>
              <li><strong>Site:</strong> https://lembrou.com.br</li>
            </ul>

            <div className="mt-8 p-4 bg-green-50 rounded-xl border border-green-200">
              <p className="text-sm text-green-800 mb-0">
                Ao utilizar o <strong>Lembrou?</strong>, você declara ter lido, compreendido e concordado 
                com esta Política de Privacidade. Seu consentimento é fundamental para que possamos 
                oferecer nossos serviços de forma segura e transparente.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            © 2026 Lembrou? — Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaPrivacidade;
